import React, { useEffect, useState } from 'react';
import {
  formatPercentBRL,
  percentNumberToString,
  stringToPercentNumber,
} from '../financialFormatters';

interface FormPercentFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
}

const FormPercentField: React.FC<FormPercentFieldProps> = ({
  id,
  name,
  value,
  onChange,
  disabled = false,
  placeholder = '0,00%',
  'aria-label': ariaLabel,
}) => {
  const formatDisplay = (raw: string) => {
    if (!raw.trim()) return '';
    return formatPercentBRL(stringToPercentNumber(raw));
  };

  const [displayValue, setDisplayValue] = useState(() => formatDisplay(value));

  useEffect(() => {
    setDisplayValue(formatDisplay(value));
  }, [value]);

  if (disabled) {
    return (
      <input
        id={id}
        name={name}
        value={displayValue}
        disabled
        readOnly
        aria-label={ariaLabel}
      />
    );
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/[^0-9]/g, '');

    if (!digitsOnly) {
      setDisplayValue('');
      onChange('');
      return;
    }

    const floatValue = parseFloat(digitsOnly) / 100;
    const formattedValue = formatPercentBRL(floatValue);
    setDisplayValue(formattedValue);
    onChange(percentNumberToString(floatValue));
  };

  const handleBlur = () => {
    setDisplayValue(formatDisplay(value));
  };

  return (
    <input
      id={id}
      name={name}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
    />
  );
};

export default FormPercentField;
